import React, { useState, useEffect, useRef } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createTour } from "@/redux/slices/tourSlice";
import { fetchCategories } from "@/redux/slices/categorySlice";
import { fetchTours } from "@/redux/slices/tourSlice";
import axios from "@/config/axiosInstance";
import { jwtDecode } from "jwt-decode";
import LoadingSpinner from "@/components/loading/LoadingSpinner";
import Map from "@/components/maps/input-map";
import "./create-tour-personalized.css";
import Layout from "../layout/layout";
import { Link } from "react-router-dom";

const CreateTourPersonalized = () => {
  const { user } = useSelector((state) => state.auth);
  const decoded = jwtDecode(user);
  const userId = decoded["sub"];

  const [tourName, setTourName] = useState("");
  const [description, setDescription] = useState("");
  const [numberDay, setNumberDay] = useState(1);
  const [maxParticipants, setMaxParticipants] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [departureLocation, setDepartureLocation] = useState("");
  const [tourDetails, setTourDetails] = useState([""]);
  const [tourImages, setTourImages] = useState([[]]); // Mảng 2D cho ảnh từng ngày
  const [validationErrors, setValidationErrors] = useState({});
  const [showAIPopup, setShowAIPopup] = useState(false);
  const [aiInput, setAIInput] = useState("");
  const [isTourDetailsReady, setIsTourDetailsReady] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isPersonalizedTour, setIsPersonalizedTour] = useState(true);

  const mainEditorRef = useRef(null);
  const detailEditorRefs = useRef([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categories, loading } = useSelector((state) => state.category);

  const MAX_IMAGES_PER_DAY = 5;

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    setIsTourDetailsReady(false);

    setTourDetails((prev) => {
      const newLength = numberDay > 0 ? numberDay : 1;
      const newTourDetails = prev.slice(0, newLength);
      if (newLength > prev.length) {
        return [...newTourDetails, ...Array(newLength - prev.length).fill("")];
      }
      return newTourDetails;
    });

    setTourImages((prev) => {
      const newLength = numberDay > 0 ? numberDay : 1;
      const newTourImages = prev.slice(0, newLength);
      if (newLength > prev.length) {
        return [...newTourImages, ...Array(newLength - prev.length).fill([])];
      }
      return newTourImages;
    });

    const timer = setTimeout(() => {
      setIsTourDetailsReady(true);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [numberDay]);

  const handlenumberDayChange = (e) => {
    const value = parseInt(e.target.value, 10);
    setNumberDay(isNaN(value) || value < 1 ? 1 : value);
  };

  const handleDescriptionChange = (index, data) => {
    const newTourDetails = [...tourDetails];
    newTourDetails[index] = data;
    setTourDetails(newTourDetails);
  };

  const handleMainEditorReady = (editor) => {
    mainEditorRef.current = editor;
    console.log("Main CKEditor initialized");
  };

  const handleDetailEditorReady = (index, editor) => {
    detailEditorRefs.current[index] = editor;
    console.log(`CKEditor initialized at index ${index}`);
  };

  const handleAIDataSubmit = () => {
    try {
      setIsTourDetailsReady(false);

      const titleMatch = aiInput.match(/LỊCH TRÌNH DU LỊCH ([^\n]+)/);
      const tourNameFromAI = titleMatch ? titleMatch[1].trim() : "New Tour";

      const dayCountMatch = aiInput.match(/(\d+) NGÀY/);
      const dayCount = dayCountMatch ? parseInt(dayCountMatch[1], 10) : 1;

      const descriptionMatch = aiInput.match(/(.+?)(?=Ngày 1)/s);
      const mainDescription = descriptionMatch
        ? descriptionMatch[1].trim()
        : "Explore a wonderful destination!";

      const daySections = aiInput.split(/(Ngày \d+ – .+?)(?=Ngày \d+|$)/s);
      const dailyDescriptions = daySections
        .filter((section) => section.match(/Ngày \d+/))
        .map((section) => {
          return section
            .replace(/Ngày \d+ – [^\n]+/, "")
            .trim()
            .replace(/\*([^\n]+)\*/g, "<strong>$1</strong>")
            .replace(/📸|🗓️|🦀|🌳|🌄|🍽️|🚠|🌺|🍲|🍜|🍤|🏖️|🍹|😊/g, "");
        });

      setTourName(tourNameFromAI);
      setDescription(mainDescription);
      setNumberDay(dayCount);
      setTourDetails(dailyDescriptions.length > 0 ? dailyDescriptions : [""]);
      setTimeout(() => setIsTourDetailsReady(true), 500);
      setShowAIPopup(false);
      setAIInput("");
      toast.success("Dữ liệu AI được tải thành công!");
    } catch (error) {
      toast.error("Lỗi khi phân tích dữ liệu AI: " + error.message);
      setIsTourDetailsReady(true);
    }
  };

  const handleImageChange = async (index, files) => {
    const currentImages = tourImages[index]?.length || 0;
    if (currentImages + files.length > MAX_IMAGES_PER_DAY) {
      toast.error(`Chỉ được upload tối đa ${MAX_IMAGES_PER_DAY} ảnh mỗi ngày!`);
      return;
    }

    setUploading(true);
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append("files", file));

    try {
      const response = await axios.post("/upload/upload-images", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newImages = response.data.images.map((img) => ({
        url: img.url,
        publicId: img.publicId,
      }));
      setTourImages((prev) => {
        const updatedImages = [...prev];
        updatedImages[index] = [...(updatedImages[index] || []), ...newImages];
        return updatedImages;
      });
      toast.success("Upload ảnh thành công!");
    } catch (error) {
      toast.error(
        "Lỗi khi upload ảnh: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (dayIndex, imageIndex) => {
    const imageToDelete = tourImages[dayIndex][imageIndex];
    try {
      await axios.delete("/upload/delete-image", {
        params: { publicId: imageToDelete.publicId },
      });
      setTourImages((prev) => {
        const newImages = [...prev];
        newImages[dayIndex] = newImages[dayIndex].filter(
          (_, i) => i !== imageIndex
        );
        return newImages;
      });
      toast.success("Đã xóa ảnh thành công!");
    } catch (error) {
      toast.error(
        "Lỗi khi xóa ảnh: " + (error.response?.data?.message || error.message)
      );
    }
  };

  const handleCreateTour = async (e) => {
    e.preventDefault();
    setValidationErrors({});

    const clientErrors = {};

    if (!tourName) clientErrors.TourName = ["Tên tour không được để trống."];
    if (!description) clientErrors.Description = ["Mô tả không được để trống."];
    if (!maxParticipants || maxParticipants <= 0)
      clientErrors.MaxParticipants = ["Số người phải lớn hơn 0."];
    if (!categoryId)
      clientErrors.CategoryId = ["Danh mục không được để trống."];
    if (!departureLocation)
      clientErrors.DepartureLocation = [
        "Địa điểm khởi hành không được để trống.",
      ];
    tourDetails.forEach((desc, index) => {
      if (!desc) {
        clientErrors[`TourDetails[${index}].Description`] = [
          "Mô tả không được để trống.",
        ];
      }
    });

    if (Object.keys(clientErrors).length > 0) {
      setValidationErrors(clientErrors);
      toast.error("Vui lòng điền đầy đủ thông tin!");
      return;
    }

    const formattedTourDetails = tourDetails.map((desc, index) => {
      // Gán ảnh mặc định nếu không có ảnh cho ngày này
      const dayImages =
        tourImages[index].length > 0
          ? tourImages[index].map((img) => ({ imageUrl: img.url }))
          : [
              {
                imageUrl:
                  "https://img.freepik.com/free-vector/illustration-gallery-icon_53876-27002.jpg?t=st=1749771494~exp=1749775094~hmac=8043e6c4775101c2f5fa11f0d7e2ad5d4ddc5b6b4ad022a90f0cb81ee2ab131a&w=1380",
              },
            ];
      return {
        dayNumber: index + 1,
        description: desc,
        tourImages: dayImages,
      };
    });

    try {
      await dispatch(
        createTour({
          tourName,
          description,
          numberDay,
          price: 1,
          maxParticipants: parseInt(maxParticipants),
          categoryId: parseInt(categoryId),
          departureLocation,
          userCreatedId: userId,
          userUpdatedId: userId,
          status: 3,
          tourDetails: formattedTourDetails,
          isPersonalizedTour: isPersonalizedTour,
        })
      ).unwrap();

      toast.success("Thêm tour thành công! 🎉");
      await dispatch(fetchTours({ page: 1, pageSize: 10 }));
      navigate("/my-tour-personalized");
    } catch (error) {
      if (error.errors) {
        setValidationErrors(error.errors);
        toast.error("Có lỗi xảy ra khi thêm tour!");
      } else {
        toast.error(error.message || "Lỗi không xác định");
      }
    }
  };

  const handleLocationChange = (newLocation) => {
    setDepartureLocation(newLocation || ""); // Đảm bảo luôn là chuỗi
  };

  return (
    <Layout>
      <div className="page-content">
        <div className="container-xxl">
          <div className="header-navigation">
            <Link to="/" className="d-inline-flex align-items-center">
              <i className="isax isax-arrow-left me-2"></i>Trang chủ
            </Link>
          </div>
          <div className="row">
            <form onSubmit={handleCreateTour}>
              <div className="col-xl-12 col-lg-12">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h4 className="card-title">
                      Tạo lịch trình tour cá nhân hóa
                    </h4>
                    <button
                      type="button"
                      className="btn btn-info"
                      onClick={() => setShowAIPopup(true)}
                    >
                      Thêm dữ liệu từ AI
                    </button>
                  </div>
                  <div className="card-body">
                    {validationErrors.general && (
                      <p className="text-danger">{validationErrors.general}</p>
                    )}
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Tên tour</label>
                          <input
                            type="text"
                            className={`form-control ${
                              validationErrors.TourName ? "is-invalid" : ""
                            }`}
                            placeholder="Nhập tên tour ..."
                            value={tourName}
                            onChange={(e) => setTourName(e.target.value)}
                          />
                          {validationErrors.TourName && (
                            <div className="invalid-feedback">
                              {validationErrors.TourName}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Danh mục</label>
                          <select
                            className={`form-control ${
                              validationErrors.CategoryId ? "is-invalid" : ""
                            }`}
                            value={categoryId}
                            onChange={(e) => setCategoryId(e.target.value)}
                          >
                            <option value="">--- Chọn danh mục tour ---</option>
                            {categories &&
                              categories.map((category) => (
                                <option key={category.id} value={category.id}>
                                  {category.categoryName}
                                </option>
                              ))}
                          </select>
                          {validationErrors.CategoryId && (
                            <div className="invalid-feedback">
                              {validationErrors.CategoryId}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Số ngày</label>
                          <input
                            type="number"
                            className={`form-control ${
                              validationErrors.NumberDay ? "is-invalid" : ""
                            }`}
                            placeholder="Nhập số ngày..."
                            value={numberDay}
                            onChange={handlenumberDayChange}
                          />
                          {validationErrors.NumberDay && (
                            <div className="invalid-feedback">
                              {validationErrors.NumberDay}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Số lượng du khách
                          </label>
                          <input
                            type="number"
                            className={`form-control ${
                              validationErrors.MaxParticipants
                                ? "is-invalid"
                                : ""
                            }`}
                            placeholder="Nhập số lượng khách tối đa..."
                            value={maxParticipants}
                            onChange={(e) => setMaxParticipants(e.target.value)}
                          />
                          {validationErrors.MaxParticipants && (
                            <div className="invalid-feedback">
                              {validationErrors.MaxParticipants}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-12">
                        <Map
                          initialLocation={departureLocation || ""}
                          onLocationChange={handleLocationChange}
                        />
                        {validationErrors.DepartureLocation && (
                          <div className="text-danger mt-1">
                            {validationErrors.DepartureLocation}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-12">
                        <div className="mb-3">
                          <label className="form-label">Mô tả</label>
                          {isTourDetailsReady && (
                            <CKEditor
                              editor={ClassicEditor}
                              data={description}
                              onReady={handleMainEditorReady}
                              onChange={(event, editor) =>
                                setDescription(editor.getData())
                              }
                              key={`main-description-${numberDay}`} // Sử dụng key để tái tạo
                            />
                          )}
                          {validationErrors.Description && (
                            <div className="text-danger mt-1">
                              {validationErrors.Description}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      {isTourDetailsReady &&
                        tourDetails.map((desc, index) => (
                          <div className="col-lg-12 mb-3" key={`day-${index}`}>
                            <div className="card p-3 border">
                              <h5>Ngày {index + 1}</h5>
                              <div className="mb-3">
                                <label className="form-label">Mô tả</label>
                                <CKEditor
                                  editor={ClassicEditor}
                                  data={desc}
                                  onReady={(editor) =>
                                    handleDetailEditorReady(index, editor)
                                  }
                                  onChange={(event, editor) =>
                                    handleDescriptionChange(
                                      index,
                                      editor.getData()
                                    )
                                  }
                                  key={`editor-${index}-${numberDay}`} // Sử dụng key để tái tạo
                                />
                                {validationErrors[
                                  `TourDetails[${index}].Description`
                                ] && (
                                  <div className="text-danger mt-1">
                                    {
                                      validationErrors[
                                        `TourDetails[${index}].Description`
                                      ]
                                    }
                                  </div>
                                )}
                              </div>
                              <div className="mb-3">
                                <label className="form-label">
                                  Chọn ảnh (Tối đa {MAX_IMAGES_PER_DAY} ảnh)
                                </label>
                                <input
                                  type="file"
                                  className={`form-control ${
                                    validationErrors[
                                      `TourDetails[${index}].TourImages`
                                    ]
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  multiple
                                  onChange={(e) =>
                                    handleImageChange(index, e.target.files)
                                  }
                                  disabled={uploading}
                                />
                                {validationErrors[
                                  `TourDetails[${index}].TourImages`
                                ] && (
                                  <div className="invalid-feedback">
                                    {
                                      validationErrors[
                                        `TourDetails[${index}].TourImages`
                                      ]
                                    }
                                  </div>
                                )}
                                {uploading && (
                                  <LoadingSpinner
                                    type="circle"
                                    color="#3498db"
                                    size={30}
                                    text="Đang tải ảnh..."
                                  />
                                )}
                                {tourImages[index]?.length > 0 && (
                                  <div className="image-preview-container mt-2">
                                    {tourImages[index].map((img, i) => (
                                      <div className="image-wrapper" key={i}>
                                        <img
                                          src={img.url}
                                          alt={`Preview ${i}`}
                                          className="preview-image"
                                        />
                                        <button
                                          className="delete-button"
                                          type="button"
                                          onClick={() =>
                                            handleDeleteImage(index, i)
                                          }
                                          title="Xóa ảnh này ra khỏi tour"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-light mb-3 rounded">
                  <div className="row justify-content-end g-2">
                    <div className="col-lg-2">
                      <a
                        href="/tour-list"
                        className="btn btn-outline-secondary w-100"
                      >
                        Hủy
                      </a>
                    </div>
                    <div className="col-lg-2">
                      <button
                        type="submit"
                        className="btn btn-primary w-100"
                        disabled={loading || uploading || !isTourDetailsReady}
                      >
                        {loading || uploading ? "Đang xử lý..." : "Thêm tour"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
          {showAIPopup && (
            <div
              className="modal"
              style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
            >
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Nhập dữ liệu từ AI</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowAIPopup(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <textarea
                      className="form-control"
                      rows="10"
                      value={aiInput}
                      onChange={(e) => setAIInput(e.target.value)}
                      placeholder="Dán dữ liệu AI tại đây..."
                    ></textarea>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowAIPopup(false)}
                    >
                      Hủy
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAIDataSubmit}
                    >
                      Áp dụng
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CreateTourPersonalized;
